export type ServitutAktSearchServitut = {
   dokumentFilnavn?: string | null;
   tekster?: Array<string | { tekst?: string | null } | null> | null;
   tekstSummarisk?: string | null;
};

export type ServitutAktSearchTargets = {
   /** Akt the servitut points at (`dokumentFilnavn`). */
   servitutAktId: string | null;
   /** Akt id parsed from tingbog one-line text, e.g. `36_Æ_95` or `1_F-IV_217`. */
   textAktId: string | null;
   /** Property-level øvrige akt. */
   ovrigAktId: string | null;
};

/**
 * Fetchable file ids: `36_Æ_95`, `36_Ø_430`, `1_F-IV_217`.
 * Three token parts separated by two underscores. The middle part may include
 * a hyphenated Roman numeral (`F-IV`).
 */
const AKT_FILE_ID_IN_TEXT_RE = /(?<![A-Za-z0-9ÆØÅæøå])(\d+[A-Za-zÆØÅæøå]*_[A-Za-zÆØÅæøå0-9]+(?:-[A-Za-zÆØÅæøå0-9]+)*_\d+)(?![A-Za-z0-9ÆØÅæøå])/gu;

/**
 * Traditional citation: `F-13-I` or `F 13-I` (letter, løbenummer, Roman).
 * Separators may be hyphen, space, or dot. Must not match datoløbenummer
 * aliases such as `23011995-904233-01`.
 */
const AKT_HYPHEN_CITATION_RE = /(?<![A-Za-z0-9ÆØÅæøå])([A-ZÆØÅ])[-.\s]+([1-9]\d{0,4})[-.\s]+([IVXLCDM]+)(?![A-Za-z0-9ÆØÅæøå])/g;

type ParsedAktParts = {
   blad: string;
   letter: string;
   roman: string;
   serial: string;
};

function trimId(value?: string | null): string | null {
   const trimmed = value?.trim();
   return trimmed ? trimmed : null;
}

function textFromTekstEntry(entry: unknown): string {
   if (typeof entry === "string") {
      return entry;
   }
   if (entry && typeof entry === "object" && "tekst" in entry) {
      const tekst = (entry as { tekst?: unknown }).tekst;
      return typeof tekst === "string" ? tekst : "";
   }
   return "";
}

export function collectServitutSearchText(servitut?: ServitutAktSearchServitut | null): string {
   if (!servitut) {
      return "";
   }
   const parts: string[] = [];
   if (Array.isArray(servitut.tekster)) {
      for (const entry of servitut.tekster) {
         const text = textFromTekstEntry(entry).trim();
         if (text) {
            parts.push(text);
         }
      }
   }
   const summary = servitut.tekstSummarisk?.trim();
   if (summary) {
      parts.push(summary);
   }
   return parts.join("\n");
}

function parseFileAktId(value?: string | null): ParsedAktParts | null {
   const trimmed = trimId(value);
   if (!trimmed) {
      return null;
   }
   const match = /^(\d+[A-Za-zÆØÅæøå]*)_([A-Za-zÆØÅæøå0-9]+(?:-[A-Za-zÆØÅæøå0-9]+)*)_(\d+)$/.exec(trimmed);
   if (!match) {
      return null;
   }
   const blad = match[1];
   const middle = match[2];
   const serial = match[3];
   const romanSplit = /^([A-Za-zÆØÅæøå0-9]+)-([IVXLCDMivxlcdm]+)$/.exec(middle);
   if (romanSplit) {
      return {
         blad,
         letter: romanSplit[1].toUpperCase(),
         roman: romanSplit[2].toUpperCase(),
         serial,
      };
   }
   return {
      blad,
      letter: middle.toUpperCase(),
      roman: "",
      serial,
   };
}

function fileIdFromParts(parts: ParsedAktParts): string {
   const middle = parts.roman ? `${parts.letter}-${parts.roman}` : parts.letter;
   return `${parts.blad}_${middle}_${parts.serial}`;
}

function aktContentKey(parts: Pick<ParsedAktParts, "letter" | "roman" | "serial">): string {
   return `${parts.letter}\0${parts.roman}\0${parts.serial}`;
}

function areEquivalentAktIds(a?: string | null, b?: string | null): boolean {
   if (!a || !b) {
      return false;
   }
   if (a === b) {
      return true;
   }
   const parsedA = parseFileAktId(a);
   const parsedB = parseFileAktId(b);
   if (parsedA && parsedB) {
      return aktContentKey(parsedA) === aktContentKey(parsedB);
   }
   return false;
}

function uniqueBlad(ids: Array<string | null | undefined>): string | null {
   const blads = new Set<string>();
   for (const id of ids) {
      const parsed = parseFileAktId(id);
      if (parsed?.blad) {
         blads.add(parsed.blad);
      }
   }
   return blads.size === 1 ? [...blads][0] : null;
}

function collectFileAktIdsFromText(text: string): string[] {
   const found: string[] = [];
   const seen = new Set<string>();
   for (const match of text.matchAll(AKT_FILE_ID_IN_TEXT_RE)) {
      const value = match[1]?.trim();
      if (!value || seen.has(value)) {
         continue;
      }
      seen.add(value);
      found.push(value);
   }
   return found;
}

function collectHyphenCitations(text: string): Array<{ letter: string; serial: string; roman: string }> {
   const found: Array<{ letter: string; serial: string; roman: string }> = [];
   const seen = new Set<string>();
   for (const match of text.matchAll(AKT_HYPHEN_CITATION_RE)) {
      const letter = match[1]?.toUpperCase();
      const serial = match[2];
      const roman = match[3]?.toUpperCase();
      if (!letter || !serial || !roman) {
         continue;
      }
      const key = aktContentKey({ letter, roman, serial });
      if (seen.has(key)) {
         continue;
      }
      seen.add(key);
      found.push({ letter, serial, roman });
   }
   return found;
}

function bladForHyphenCitation(
   citation: { letter: string; serial: string; roman: string },
   fileIdsInText: string[],
   bladHints: Array<string | null | undefined>,
): string | null {
   const hintIds = [...fileIdsInText, ...bladHints];
   const matching = hintIds
      .map((id) => parseFileAktId(id))
      .find((parsed) => parsed && aktContentKey(parsed) === aktContentKey(citation));
   if (matching) {
      return matching.blad;
   }
   return uniqueBlad(hintIds);
}

/**
 * File-id form only (`1_F-I_13`). Citations such as `F-13-I` or `F 13-I` are
 * converted when a blad number can be inferred from the same text or from `bladHints`.
 */
export function extractAktIdsFromServitutText(
   servitut?: ServitutAktSearchServitut | null,
   bladHints?: Array<string | null | undefined>,
): string[] {
   const text = collectServitutSearchText(servitut);
   if (!text) {
      return [];
   }
   const found: string[] = [];
   const seenIds = new Set<string>();
   const seenContent = new Set<string>();

   const addFileId = (fileId: string) => {
      if (seenIds.has(fileId)) {
         return;
      }
      const parsed = parseFileAktId(fileId);
      const contentKey = parsed ? aktContentKey(parsed) : fileId;
      if (seenContent.has(contentKey)) {
         return;
      }
      seenIds.add(fileId);
      seenContent.add(contentKey);
      found.push(fileId);
   };

   const fileIdsInText = collectFileAktIdsFromText(text);
   for (const fileId of fileIdsInText) {
      addFileId(fileId);
   }

   for (const citation of collectHyphenCitations(text)) {
      const blad = bladForHyphenCitation(citation, fileIdsInText, bladHints || []);
      if (!blad) {
         continue;
      }
      addFileId(fileIdFromParts({ blad, ...citation }));
   }

   return found;
}

export function getServitutAktSearchTargets(
   servitut?: ServitutAktSearchServitut | null,
   stamOplysninger?: { indskannetAktDokumentId?: string | null } | null,
): ServitutAktSearchTargets {
   const servitutAktId = trimId(servitut?.dokumentFilnavn);
   const ovrigAktId = trimId(stamOplysninger?.indskannetAktDokumentId);
   const textAktId = extractAktIdsFromServitutText(servitut, [servitutAktId, ovrigAktId])
      .find((aktId) => !areEquivalentAktIds(aktId, servitutAktId)) || null;
   return {
      servitutAktId,
      textAktId,
      ovrigAktId,
   };
}

/** Pointed akt, then regex akt in the servitut text, then øvrige akt. Never overrides metadata. */
export function getServitutPdfFetchAktId(
   servitut?: ServitutAktSearchServitut | null,
   stamOplysninger?: { indskannetAktDokumentId?: string | null } | null,
): string | null {
   const { servitutAktId, textAktId, ovrigAktId } = getServitutAktSearchTargets(servitut, stamOplysninger);
   return servitutAktId || textAktId || ovrigAktId || null;
}

export function isServitutFromOvrigAktSource(
   servitut?: ServitutAktSearchServitut | null,
   stamOplysninger?: { indskannetAktDokumentId?: string | null } | null,
): boolean {
   const { servitutAktId, textAktId, ovrigAktId } = getServitutAktSearchTargets(servitut, stamOplysninger);
   return !servitutAktId && !textAktId && Boolean(ovrigAktId);
}

export function isServitutFromTextAktSource(
   servitut?: ServitutAktSearchServitut | null,
   stamOplysninger?: { indskannetAktDokumentId?: string | null } | null,
): boolean {
   const { servitutAktId, textAktId } = getServitutAktSearchTargets(servitut, stamOplysninger);
   return !servitutAktId && Boolean(textAktId);
}
